
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Typography,
  Card,
  CardContent,
  TextField,
  Stack,
  Drawer,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import SearchIcon          from "@mui/icons-material/Search";
import PlayCircleIcon      from "@mui/icons-material/PlayCircleFilled";
import ArticleIcon         from "@mui/icons-material/Article";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon      from "@mui/icons-material/Visibility";
import BookmarkIcon        from "@mui/icons-material/Bookmark";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import ScienceIcon         from "@mui/icons-material/Science";
import ExpandMoreIcon      from "@mui/icons-material/ExpandMore";
import FilterListIcon      from "@mui/icons-material/FilterList";
import CloseIcon           from "@mui/icons-material/Close";
import TopBar              from "./topbar";
import Footer              from "./footer";

const ITEMS_PER_PAGE = 4;

const initialFilterState = {
  articles:   false,
  videos:     false,
  templates:  false,
  caseStudies: false,
  businessstrategy:              false,
  marketingsales:                false,
  financefunding:                false,
  entrepreneurship:              false,
  technologyai:                  false,
  trendingresources:             false,
  aipersonalizedrecommendations: false,
  newestfirst:                   false,
  oldestfirst:                   false,
};

export default function ResourceLibrary() {
  const [resources,         setResources]         = useState([]);
  const [visibleResources,  setVisibleResources]  = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [currentPage,       setCurrentPage]       = useState(1);
  const [pendingFilters,    setPendingFilters]     = useState(initialFilterState);
  const [appliedFilters,    setAppliedFilters]     = useState(initialFilterState);
  const [search,            setSearch]            = useState("");
  const [filterDrawerOpen,  setFilterDrawerOpen]  = useState(false);

  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // < 900px

  const handleChange = (key) => {
    setPendingFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const applyFilters = () => {
    setAppliedFilters(pendingFilters);
    setCurrentPage(1);
    if (isMobile) setFilterDrawerOpen(false);
  };

  const clearFilters = () => {
    setPendingFilters(initialFilterState);
    setAppliedFilters(initialFilterState);
    setSearch("");
    setCurrentPage(1);
    if (isMobile) setFilterDrawerOpen(false);
  };

  useEffect(() => {
    (async () => {
      const res  = await fetch("https://backend-global-1vq9.onrender.com/api/resources");
      const data = await res.json();
      setResources(data);
    })();
  }, []);

  useEffect(() => {
    let filtered = [...resources];

    if (search.trim() !== "") {
      filtered = filtered.filter((r) =>
        r.title.toLowerCase().includes(search.toLowerCase())
      );
    }

    const typeMap = {
      articles:   "article",
      videos:     "video",
      templates:  "template",
      caseStudies: "case-study",
    };
    const selectedTypes = Object.keys(typeMap).filter((key) => appliedFilters[key]);
    if (selectedTypes.length > 0) {
      filtered = filtered.filter((item) =>
        selectedTypes.includes(
          Object.keys(typeMap).find((k) => typeMap[k] === item.type)
        )
      );
    }

    setFilteredResources(filtered);
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    setVisibleResources(filtered.slice(start, start + ITEMS_PER_PAGE));
  }, [appliedFilters, search, resources, currentPage]);

  const totalPages         = Math.ceil(filteredResources.length / ITEMS_PER_PAGE);
  const activeFilterCount  = Object.values(pendingFilters).filter(Boolean).length + (search.trim() ? 1 : 0);

  // ── Filter panel — shared between sidebar (desktop) and drawer (mobile) ──
  const FilterPanel = () => (
    <Box sx={{ width: "100%", boxSizing: "border-box" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
          Library Filters
        </Typography>
        {isMobile && (
          <IconButton size="small" onClick={() => setFilterDrawerOpen(false)} sx={{ color: "#979797" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ width: "100%", mb: 2 }} />

      {/* Content type */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Content Type
      </Typography>
      <FormGroup sx={{ width: "100%", mb: 2 }}>
        <FormControlLabel control={<Checkbox checked={pendingFilters.articles}   onChange={() => handleChange("articles")}   icon={<ArticleIcon />}         checkedIcon={<ArticleIcon color="primary" />} />}         label="Articles"     />
        <FormControlLabel control={<Checkbox checked={pendingFilters.videos}     onChange={() => handleChange("videos")}     icon={<PlayCircleFilledIcon />} checkedIcon={<PlayCircleFilledIcon color="primary" />} />} label="Videos"       />
        <FormControlLabel control={<Checkbox checked={pendingFilters.templates}  onChange={() => handleChange("templates")}  icon={<InsertDriveFileIcon />}  checkedIcon={<InsertDriveFileIcon color="primary" />} />}  label="Templates"    />
        <FormControlLabel control={<Checkbox checked={pendingFilters.caseStudies} onChange={() => handleChange("caseStudies")} icon={<ScienceIcon />}         checkedIcon={<ScienceIcon color="primary" />} />}         label="Case Studies" />
      </FormGroup>

      <Divider sx={{ width: "100%", mb: 2 }} />

      {/* Category */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">Category</Typography>
        <IconButton size="small"><ExpandMoreIcon /></IconButton>
      </Box>
      <FormGroup sx={{ width: "100%", mb: 2 }}>
        {["Business Strategy", "Marketing & Sales", "Finance & Funding", "Entrepreneurship", "Technology & AI"].map((label, i) => (
          <FormControlLabel
            key={i}
            control={
              <Checkbox
                checked={pendingFilters[label.toLowerCase().replace(/ & | /g, "")]}
                onChange={() => handleChange(label.toLowerCase().replace(/ & | /g, ""))}
              />
            }
            label={label}
          />
        ))}
      </FormGroup>

      <Divider sx={{ width: "100%", mb: 2 }} />

      {/* Popularity */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Popularity & Relevance
      </Typography>
      <FormGroup sx={{ width: "100%", mb: 3 }}>
        {["Trending Resources", "Most Downloaded", "AI-Personalized Recommendations", "Newest First", "Oldest First"].map((label, i) => (
          <FormControlLabel
            key={i}
            control={
              <Checkbox
                checked={pendingFilters[label.toLowerCase().replace(/ |-/g, "")]}
                onChange={() => handleChange(label.toLowerCase().replace(/ |-/g, ""))}
              />
            }
            label={label}
          />
        ))}
      </FormGroup>

      {/* Action buttons */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
        <Button
          variant="outlined" fullWidth
          sx={{ borderColor: "transparent", fontWeight: "bold", color: "#6BD0F6", textTransform: "none", py: 1.5 }}
          onClick={clearFilters}
        >
          Clear
        </Button>
        <Button
          variant="contained" fullWidth
          sx={{ background: "linear-gradient(180deg,#6BD0F6,#00AEEF)", fontWeight: "bold", textTransform: "none", py: 1.5 }}
          onClick={applyFilters}
        >
          Apply
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", overflowX: "hidden", width: "100%", boxSizing: "border-box" }}>
      <TopBar />

      {/* ── Main layout ───────────────────────────────────────────────── */}
      <Box sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 2, md: 4 },
        px: { xs: "12px", sm: "24px", md: "32px" },
        py: { xs: 3, sm: 4 },
        flex: 1,
        width: "100%",
        boxSizing: "border-box",
      }}>

        {/* ── LEFT: Resources section ───────────────────────────────── */}
        <Box sx={{ flex: 3, minWidth: 0, width: "100%", boxSizing: "border-box" }}>

          {/* Search bar + filter toggle (mobile) */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search resources..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "#6C6C6C" }} /> }}
              sx={{ borderRadius: 20, "& .MuiOutlinedInput-root": { borderRadius: 20 } }}
            />
            {/* Filter button — mobile only */}
            {isMobile && (
              <Button
                size="small"
                startIcon={<FilterListIcon sx={{ fontSize: 15 }} />}
                onClick={() => setFilterDrawerOpen(true)}
                sx={{
                  flexShrink: 0,
                  bgcolor: "#E6F7FD", color: "#00AEEF",
                  textTransform: "none", fontWeight: "bold",
                  fontSize: 12, borderRadius: 20,
                  px: 1.5, py: 1, whiteSpace: "nowrap",
                }}
              >
                Filters
                {activeFilterCount > 0 && (
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#F57E47", ml: 0.5 }} />
                )}
              </Button>
            )}
          </Stack>

          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, fontSize: { xs: 15, sm: 18 } }}>
            All Resources
          </Typography>

          {visibleResources.length === 0 && (
            <Typography sx={{ color: "#6C6C6C", fontSize: 14 }}>No resources found</Typography>
          )}

          {/* ── Resource cards ───────────────────────────────────── */}
          {visibleResources.map((resource) => (
            <Card key={resource._id} variant="outlined" sx={{ borderRadius: 3, mb: 2, width: "100%", boxSizing: "border-box" }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="flex-start" mb={1}>
                  {/* Type icon */}
                  <Box sx={{ flexShrink: 0 }}>
                    {resource.type === "video"      && <PlayCircleIcon      sx={{ fontSize: { xs: 36, sm: 48 }, color: "#F57E47" }} />}
                    {resource.type === "article"    && <ArticleIcon         sx={{ fontSize: { xs: 36, sm: 48 }, color: "#00AEEF" }} />}
                    {resource.type === "template"   && <InsertDriveFileIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: "#00AEEF" }} />}
                    {resource.type === "case-study" && <InsertDriveFileIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: "#6C6C6C" }} />}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Title + type badge */}
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" gap={0.5} mb={0.5}>
                      <Typography sx={{ fontWeight: "bold", fontSize: { xs: 12, sm: 14 }, lineHeight: 1.4 }}>
                        {resource.title}
                      </Typography>
                      <Box sx={{
                        background: resource.type === "video"
                          ? "linear-gradient(180deg,#F57E47,#F36421)"
                          : "linear-gradient(180deg,#6BD0F6,#00AEEF)",
                        borderRadius: 1, px: 0.5, py: 0.25, flexShrink: 0,
                      }}>
                        <Typography sx={{ color: "#fff", fontSize: 9, fontWeight: "bold" }}>
                          {resource.type.charAt(0).toUpperCase() + resource.type.slice(1)}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Description — clamped to 2 lines on mobile */}
                    <Typography sx={{
                      color: "#6C6C6C", fontSize: { xs: 11, sm: 12 },
                      mt: 0.5,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: { xs: 2, sm: 3 },
                      WebkitBoxOrient: "vertical",
                    }}>
                      {resource.description}
                    </Typography>
                  </Box>
                </Stack>

                {/* Category */}
                <Typography sx={{ fontSize: 10, fontWeight: "bold", color: "#2C2C2C", mb: 1 }}>
                  Category: {resource.category}
                </Typography>

                {/* Action buttons */}
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    startIcon={<VisibilityIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                    onClick={() => navigate(`/resources/${resource._id}`)}
                    sx={{ color: "#00AEEF", fontWeight: "bold", fontSize: { xs: 11, sm: 12 }, textTransform: "none", p: { xs: "4px 8px", sm: "6px 12px" } }}
                  >
                    View
                  </Button>
                  <Button
                    startIcon={<BookmarkIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                    onClick={async () => {
                      const user   = JSON.parse(localStorage.getItem("user"));
                      const userId = user?.id;
                      if (!userId) { alert("No user ID found. Please log in."); return; }
                      try {
                        await fetch("https://backend-global-1vq9.onrender.com/api/bookmarks/add", {
                          method:  "POST",
                          headers: { "Content-Type": "application/json" },
                          body:    JSON.stringify({ userId, resourceId: resource._id }),
                        });
                        alert("Resource bookmarked successfully!");
                      } catch (err) {
                        alert("Failed to bookmark resource.");
                      }
                    }}
                    sx={{ color: "#00AEEF", fontWeight: "bold", fontSize: { xs: 11, sm: 12 }, textTransform: "none", p: { xs: "4px 8px", sm: "6px 12px" } }}
                  >
                    Bookmark
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}

          {/* ── Pagination ────────────────────────────────────────── */}
          {visibleResources.length < resources.length && (
            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                sx={{ fontSize: { xs: 12, sm: 14 }, borderColor: "#00AEEF", color: "#00AEEF", "&:disabled": { borderColor: "#E6E6E6", color: "#979797" } }}
              >
                Prev
              </Button>
              <Typography sx={{ color: "#6C6C6C", fontSize: { xs: 12, sm: 14 } }}>
                Page {currentPage} of {totalPages}
              </Typography>
              <Button
                variant="contained"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                sx={{ fontSize: { xs: 12, sm: 14 }, background: "linear-gradient(180deg,#6BD0F6,#00AEEF)", "&:disabled": { bgcolor: "#E6E6E6" } }}
              >
                Next
              </Button>
            </Stack>
          )}
        </Box>

        {/* ── RIGHT: Filter panel — desktop sidebar only ────────────── */}
        {!isMobile && (
          <Box sx={{
            flex: 1.2,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            px: 2,
            position: "sticky",
            top: 20,
            height: "fit-content",
            width: { md: 260, lg: 300 },
            boxSizing: "border-box",
          }}>
            <FilterPanel />
          </Box>
        )}
      </Box>

      <Footer />

      {/* ── Mobile: filter bottom drawer ─────────────────────────────── */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius:  20,
              borderTopRightRadius: 20,
              maxHeight: "88vh",
              overflowY: "auto",
              px: "16px",
              pt: "16px",
              pb: "32px",
            },
          }}
        >
          {/* Drag handle */}
          <Box sx={{ width: 40, height: 4, bgcolor: "#E6E6E6", borderRadius: 2, mx: "auto", mb: 2 }} />
          <FilterPanel />
        </Drawer>
      )}
    </Box>
  );
}
